import { useFormikContext } from 'formik';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';

import { useSources } from '@/react/portainer/gitops/sources/queries/useSources';

import { AddButton, Button } from '@@/buttons';
import { TextTip } from '@@/Tip/TextTip';

import { artifactsStepValidationSchema } from '../../validation';
import { EngineFieldset } from '../../EngineFieldset';
import { WizardMode, WizardStep, useWizardContext } from '../../WizardContext';
import { FormValues } from '../../types';
import { StepTitle } from '../StepTitle';

import { ArtifactGroup } from './ArtifactGroup';
import { FileChip } from './ArtifactsSummary';
import { useArtifactList } from './useArtifactList';
import { useArtifactDrag } from './useArtifactDrag';
import { renameArtifactAt } from './artifactValues';
import { ResizableColumns } from './ResizableColumns';
import { SourceSelector } from './SourceSelector';

export const ArtifactsStep: WizardStep = {
  id: 'artifacts',
  label: 'Sources & artifacts',
  Component: ArtifactsStepComponent,
  validateStep,
};

function ArtifactsStepComponent() {
  const { values, setValues, errors } = useFormikContext<FormValues>();
  const { mode, setArtifactCount } = useWizardContext();
  const isEditing = mode === WizardMode.Edit;
  const { activeIndex, activate, artifactKeys, addArtifact, removeArtifact } =
    useArtifactList(setArtifactCount);
  const activeFiles = values.artifacts[activeIndex]?.files ?? [];
  const sourcesQuery = useSources({ type: 'git' });
  const gitSources = sourcesQuery.data?.data ?? [];
  const sourceNameById = useMemo(
    () =>
      new Map(
        sourcesQuery.data?.data?.map((source) => [source.id, source.name])
      ),
    [sourcesQuery.data]
  );
  const {
    sensors,
    draggedFile,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = useArtifactDrag(activate);

  if (sourcesQuery.isLoading) {
    return null;
  }

  return (
    <>
      <StepTitle
        title="Configure sources & artifacts"
        subtitle={
          isEditing
            ? 'Browse your sources and select a file for each stack. Artifacts cannot be added, removed, or renamed once a workflow is created.'
            : 'Browse your sources and select a file to build one or more stacks. Add an artifact for each stack this workflow should deploy, and set which artifacts depend on others to control the deploy order.'
        }
      />

      <EngineFieldset />

      {/* Both describe how an Edge stack assembles a bundle: one checkout, and
          an entry file the rest follow. The reconciliation engine has neither
          - it draws from several sources at once and applies whatever its
          paths resolve to, in no particular order. */}
      {values.engine !== 'reconcile' && (
        <>
          <TextTip color="blue" className="mb-4">
            An artifact can only contain files from a single source. Artifacts
            can use any combinations of sources and references.
          </TextTip>
          <br />
          <TextTip color="blue" className="mb-4">
            Artifacts with multiple files are deployed with the specified file
            order. The first file of the list acts as the entrypoint.
          </TextTip>
        </>
      )}

      {gitSources.length === 0 ? (
        <div className="flex items-center justify-between gap-4">
          <TextTip color="blue">
            No Git sources available. Create one to continue.
          </TextTip>
          <AddButton
            to="portainer.gitops.sources.new"
            data-cy="create-source-button"
          >
            Create new source
          </AddButton>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <ResizableColumns
            left={
              <SourceSelector
                gitSources={gitSources}
                activeIndex={activeIndex}
                activeFiles={activeFiles}
              />
            }
            right={
              <>
                {values.artifacts.map((artifact, index) => (
                  <ArtifactGroup
                    key={artifactKeys[index]}
                    artifact={artifact}
                    index={index}
                    sourceNameById={sourceNameById}
                    otherArtifactNames={values.artifacts
                      .filter((_, i) => i !== index)
                      .map((other) => other.name)
                      .filter(Boolean)}
                    isActive={index === activeIndex}
                    isNameReadOnly={isEditing}
                    canRemove={!isEditing && values.artifacts.length > 1}
                    onActivate={() => activate(index)}
                    onChange={(changes) =>
                      setValues((prev) =>
                        renameArtifactAt(prev, index, changes)
                      )
                    }
                    onRemove={() => removeArtifact(index)}
                    errors={errors.artifacts?.[index]}
                  />
                ))}

                {!isEditing && (
                  <Button
                    type="button"
                    color="default"
                    icon={Plus}
                    onClick={addArtifact}
                    data-cy="artifact-add-button"
                  >
                    Add artifact
                  </Button>
                )}
              </>
            }
          />

          <DragOverlay>
            {draggedFile && (
              <FileChip
                file={draggedFile}
                sourceName={sourceNameById.get(draggedFile.sourceId)}
                className="rounded border border-solid border-gray-4 shadow-md th-highcontrast:border-white th-dark:border-gray-7"
              />
            )}
          </DragOverlay>
        </DndContext>
      )}
    </>
  );
}

function validateStep() {
  return artifactsStepValidationSchema();
}
